import React, { Component } from "react";
import { Dialog } from "primereact/dialog";
import { RequestLog } from "../../models/Journaling";
import moment from "moment";
import { AppCard } from "../../components/ui";
import Loader from "../../components/Loader";
import ReactJson from "react-json-view";

interface RequestLogDetailsDialogProps {
    isVisible: boolean;
    onHide: () => void;
    requestLog: RequestLog;
    editable: boolean;
    userEmail: string | undefined;
    merchantName: string | undefined;
}

interface RequestLogDetailsDialogState {
    isDetailVisible: boolean;
    requestLog: RequestLog;
}

export default class RequestLogDetailsDialog extends Component<RequestLogDetailsDialogProps, RequestLogDetailsDialogState> {
    constructor(props: RequestLogDetailsDialogProps | Readonly<RequestLogDetailsDialogProps>) {
        super(props);

        this.state = {
            isDetailVisible: false,
            requestLog: this.props.requestLog,
        };
    }

    toggleAdvancedMode = () => {
        this.setState({ isDetailVisible: !this.state.isDetailVisible });
    };

    render() {
        if (this.state.isDetailVisible) {
            return <Loader />;
        }

        if (this.state.requestLog == null || !this.props.isVisible) {
            return null;
        }

        const log = this.state.requestLog;

        return (
            <Dialog header="Request Log" visible={this.props.isVisible} style={{ width: "80vw" }} onHide={this.props.onHide}>
                <div className="space-y-4">
                    <AppCard>
                        <AppCard.Body>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DetailItem label="Controller Name" value={log.controllerName} />
                                <DetailItem label="Action Name" value={log.actionName} />
                                <DetailItem label="Made On" value={moment.utc(log.madeOn).local().format("DD/MM/YYYY HH:mm:ss")} />
                                <DetailItem label="Application" value={log.application} />
                                <DetailItem label="User" value={this.props.userEmail || log.userId} />
                                <DetailItem label="IP Address" value={log.madeFromIPAddress} />
                                <DetailItem label="Merchant" value={this.props.merchantName || log.merchantId} />
                                <DetailItem label="Status Code" value={log.statusCode} />
                            </div>
                        </AppCard.Body>
                    </AppCard>

                    <AppCard>
                        <AppCard.Header title="Data Info" />
                        <AppCard.Body>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Request</span>
                                    <JsonOrText value={log.request === "null" ? "" : log.request} />
                                </div>
                                <div>
                                    <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Response</span>
                                    <JsonOrText value={log.response} />
                                </div>
                            </div>
                        </AppCard.Body>
                    </AppCard>
                </div>
            </Dialog>
        );
    }
}

function DetailItem({ label, value }: { label: string; value: any }) {
    return (
        <div>
            <span className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">{label}</span>
            <span className="text-sm text-neutral-900 dark:text-neutral-100 break-all">{value ?? "—"}</span>
        </div>
    );
}

function JsonOrText({ value }: { value: string }) {
    if (!value) return <span className="text-sm text-neutral-400">—</span>;
    if (isValidJSON(value)) {
        return (
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3 overflow-auto max-h-80">
                <ReactJson src={JSON.parse(value)} />
            </div>
        );
    }
    return <span className="text-sm text-neutral-900 dark:text-neutral-100 break-all">{value}</span>;
}

export function isValidJSON(obj: any) {
    const parsed = ParseSafe(obj);
    return toType(parsed) == "object" || toType(parsed) == "array";
}

export function toType(obj: any) {
    let type = getType(obj);
    if (type === "number") {
        if (isNaN(obj)) {
            type = "nan";
        } else if ((obj | 0) != obj) {
            type = "float";
        } else {
            type = "integer";
        }
    }
    return type;
}

export function ParseSafe(obj: string) {
    try {
        return JSON.parse(obj);
    } catch (error) {
        return null;
    }
}

function getType(obj: any) {
    return ({} as any).toString
        .call(obj)
        .match(/\s([a-zA-Z]+)/)[1]
        .toLowerCase();
}

export function renderDualRow(title1: string, val1: string, title2: string | null = null, val2: string = null as any, prettyFormat: boolean = false) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
            <div>
                <span className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">{title1}</span>
                {val1 && (
                    prettyFormat && isValidJSON(val1) ? (
                        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3 overflow-auto max-h-80">
                            <ReactJson src={JSON.parse(val1)} />
                        </div>
                    ) : (
                        <span className="text-sm text-neutral-900 dark:text-neutral-100 break-all">{val1}</span>
                    )
                )}
            </div>
            {title2 != null && (
                <div>
                    <span className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">{title2}</span>
                    {val2 && (
                        prettyFormat && isValidJSON(val2) ? (
                            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3 overflow-auto max-h-80">
                                <ReactJson src={JSON.parse(val2)} />
                            </div>
                        ) : (
                            <span className="text-sm text-neutral-900 dark:text-neutral-100 break-all">{val2}</span>
                        )
                    )}
                </div>
            )}
        </div>
    );
}
